class StickyAddToCart {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.stickySectionId = container.dataset.stickySectionId;
    this.mainSection = document.getElementById(`MainProduct-${this.sectionId}`);
    this.mainForm = document.getElementById(`product-form-${this.sectionId}`);
    this.mainVariantSelects = this.mainSection?.querySelector(
      `variant-selects[data-section="${this.sectionId}"]:not([data-sticky-atc])`
    );
    this.stickyVariantSelects = container.querySelector('variant-selects[data-sticky-atc]');
    this.mainQuantityInput = this.mainSection?.querySelector(`#Quantity-${this.sectionId}`);
    this.stickyQuantityInput = container.querySelector('.quantity__input');
    this.mainVariantIdInput = this.mainForm?.querySelector('input[name="id"]');
    this.stickyVariantIdInput = container.querySelector('input[name="id"]');
    this.mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    this.stickySubmitButton = container.querySelector('[data-sticky-atc-submit]');
    this.anchor = this.mainSection?.querySelector('[data-sticky-atc-anchor]');
    this.mainPrice = document.getElementById(`price-${this.sectionId}`);
    this.stickyPrice = container.querySelector('[data-sticky-atc-price]');
    this.isSyncing = false;
  }

  init() {
    if (!this.mainSection || !this.mainForm || !this.anchor) return;

    this.setContainerHeight();
    this.observeAnchor();
    this.bindEvents();
    this.syncFromMain();
    this.updateInitialVisibility();
  }

  bindEvents() {
    if (this.mainQuantityInput && this.stickyQuantityInput) {
      this.mainQuantityInput.addEventListener('change', () => {
        this.handleQuantitySync(this.mainQuantityInput, this.stickyQuantityInput);
      });
      this.stickyQuantityInput.addEventListener('change', () => {
        this.handleQuantitySync(this.stickyQuantityInput, this.mainQuantityInput);
      });
    }

    if (this.mainVariantSelects && this.stickyVariantSelects) {
      this.mainVariantSelects.addEventListener('change', () => {
        this.handleVariantSync(this.mainVariantSelects, this.stickyVariantSelects);
      });
      this.stickyVariantSelects.addEventListener('change', () => {
        this.handleVariantSync(this.stickyVariantSelects, this.mainVariantSelects);
      });
    }

    if (this.mainVariantIdInput) {
      this.mainVariantIdInput.addEventListener('change', () => this.syncVariantId());
    }

    if (this.mainSubmitButton) {
      const observer = new MutationObserver(() => this.syncButtonState());
      observer.observe(this.mainSubmitButton, { attributes: true, childList: true, subtree: true });
    }

    if (typeof subscribe === 'function') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (!event?.data?.sectionId || event.data.sectionId !== this.sectionId) return;
        this.syncFromMain();
      });
      this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
        this.syncFromMain();
      });
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.syncFromMain();
      });
    }

    window.addEventListener('resize', () => this.setContainerHeight());
  }

  observeAnchor() {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const shouldShow = this.shouldShowSticky(entry);
        this.toggleSticky(shouldShow);
      },
      {
        threshold: 0,
      }
    );

    observer.observe(this.anchor);
  }

  updateInitialVisibility() {
    if (!this.anchor) return;
    const anchorRect = this.anchor.getBoundingClientRect();
    const shouldShow = anchorRect.bottom < 0;
    this.toggleSticky(shouldShow);
  }

  toggleSticky(shouldShow) {
    this.setContainerHeight();
    this.container.classList.toggle('is-visible', shouldShow);
    this.container.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    document.body?.classList.toggle('sticky-atc-visible', shouldShow);
  }

  shouldShowSticky(entry) {
    if (!entry) return false;
    if (!entry.isIntersecting) {
      return entry.boundingClientRect.bottom < 0;
    }
    return false;
  }

  setContainerHeight() {
    const height = this.container.offsetHeight;
    this.mainSection.style.setProperty('--sticky-atc-height', `${height}px`);
    document.documentElement.style.setProperty('--sticky-atc-mobile-offset', `${height}px`);
  }

  handleQuantitySync(source, target) {
    if (this.isSyncing || !source || !target) return;
    this.isSyncing = true;
    target.value = source.value;
    target.dispatchEvent(new Event('change', { bubbles: true }));
    this.isSyncing = false;
  }

  handleVariantSync(source, target) {
    if (this.isSyncing || !source || !target) return;
    this.isSyncing = true;
    this.syncVariantSelections(source, target);
    this.syncVariantId();
    this.syncButtonState();
    this.isSyncing = false;
  }

  syncFromMain() {
    this.syncVariantSelections(this.mainVariantSelects, this.stickyVariantSelects);
    this.syncQuantityAttributes();
    this.syncQuantityValue(this.mainQuantityInput, this.stickyQuantityInput);
    this.syncVariantId();
    this.syncButtonState();
    this.syncPrice();
    this.setContainerHeight();
  }

  syncQuantityAttributes() {
    if (!this.mainQuantityInput || !this.stickyQuantityInput) return;
    const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'min', 'max', 'step'];
    attributes.forEach((attribute) => {
      const value = this.mainQuantityInput.getAttribute(attribute);
      if (value === null) {
        this.stickyQuantityInput.removeAttribute(attribute);
      } else {
        this.stickyQuantityInput.setAttribute(attribute, value);
      }
    });
  }

  syncQuantityValue(source, target) {
    if (!source || !target) return;
    target.value = source.value;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  syncVariantId() {
    if (!this.mainVariantIdInput || !this.stickyVariantIdInput) return;
    this.stickyVariantIdInput.value = this.mainVariantIdInput.value;
  }

  syncButtonState() {
    if (!this.mainSubmitButton || !this.stickySubmitButton) return;
    const isDisabled = this.mainSubmitButton.hasAttribute('disabled');
    if (isDisabled) {
      this.stickySubmitButton.setAttribute('disabled', 'disabled');
    } else {
      this.stickySubmitButton.removeAttribute('disabled');
    }

    const mainText = this.mainSubmitButton.querySelector('span');
    const stickyText = this.stickySubmitButton.querySelector('span');
    if (mainText && stickyText) {
      stickyText.textContent = mainText.textContent;
    }
  }

  syncPrice() {
    if (!this.mainPrice || !this.stickyPrice) return;
    this.stickyPrice.innerHTML = this.mainPrice.innerHTML;
    this.stickyPrice.classList.toggle('hidden', this.mainPrice.classList.contains('hidden'));

    const skuElement = this.stickyPrice.querySelector('[id^="Sku-"]');
    if (skuElement && this.stickySectionId) {
      skuElement.id = `Sku-${this.stickySectionId}`;
    }
  }

  syncVariantSelections(source, target) {
    if (!source || !target) return;
    const sourceOptions = Array.from(source.querySelectorAll('select, fieldset'), (element) => {
      if (element.tagName === 'SELECT') {
        return element.value;
      }
      if (element.tagName === 'FIELDSET') {
        return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
      }
      return undefined;
    });

    const targetOptions = Array.from(target.querySelectorAll('select, fieldset'));
    const escapeValue = (value) => {
      if (window.CSS && CSS.escape) return CSS.escape(value);
      return value.replace(/"/g, '\\"');
    };

    sourceOptions.forEach((value, index) => {
      const targetElement = targetOptions[index];
      if (!targetElement || value === undefined) return;
      if (targetElement.tagName === 'SELECT') {
        targetElement.value = value;
      } else if (targetElement.tagName === 'FIELDSET') {
        const selector = `input[value="${escapeValue(value)}"]`;
        const input = targetElement.querySelector(selector);
        if (input) input.checked = true;
      }
    });

    if (typeof target.getVariantData === 'function') {
      target.getVariantData();
    }

    if (typeof target.updateSelectedSwatchValue === 'function') {
      target.querySelectorAll('select').forEach((select) => {
        target.updateSelectedSwatchValue({ target: select });
      });
      target.querySelectorAll('fieldset').forEach((fieldset) => {
        const input = fieldset.querySelector('input:checked');
        if (input) target.updateSelectedSwatchValue({ target: input });
      });
    }

    if (typeof target.updateVariantStatuses === 'function') {
      target.updateVariantStatuses();
    }
  }
}

document.querySelectorAll('[data-sticky-atc]').forEach((container) => {
  const sticky = new StickyAddToCart(container);
  sticky.init();
});
